import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { MRT_ColumnDef } from "material-react-table";
import FormBuilder from "../../../../../../../shared/components/form-builder/form-builder";
import {
  ITCProduct,
  Option,
  transformShipmentDetails,
} from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { mapToIShipmentConsigneeDetails } from "../../../../tc-models/tc-req-dto/tc-shipments.req_dto";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { Box, Typography } from "@mui/material";
interface ProductInfoProps {
  productData: ITCProduct;
  setProductData: React.Dispatch<React.SetStateAction<ITCProduct>>;
  shipmentId: string;
  formRef: any;
  // WITH-component mode: hide Additives Weight + Net Shipping Weight (both derived from
  // the components) and skip their validation. Defaults to the without-component flow.
  withComponent?: boolean;
}

const disallowedPlaceholderValues = new Set([
  "na",
  "n/a",
  "not available",
  "null",
]);

const isBlankOrPlaceholder = (value: unknown) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return !normalized || disallowedPlaceholderValues.has(normalized);
};

const isOtherUnitsMetric = (value: unknown) =>
  String(value ?? "").trim().toLowerCase() === "other";

// Remove duplicate dropdown options by value (the seller-SC-scoped ref-code response can repeat
// the same code, and we never want the same code listed twice).
const dedupeOptions = (options: any[]) => {
  const seen = new Set<string>();
  return options.filter((opt) => {
    const key = String(opt?.value ?? "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Per DAT-501 the PC/PD dropdowns offer Seller-SC-scoped codes only. A
// persisted draft can still hold a code outside that scope (legacy data saved
// before the scope rule was enforced); such a value renders unselected and is
// rejected by the scope-membership validation below so the user must re-point
// it at a scoped code. The check is skipped while the scoped list is empty
// (still loading, or an SC without scope data) — the submit-side rule owns
// enforcement in that case.
const isInScope = (options: any[], code: unknown) => {
  const value = String(code ?? "").trim();
  if (!value || options.length === 0) return true;
  return options.some((opt) => String(opt?.value ?? "") === value);
};

const ProductInfo: React.FC<ProductInfoProps> = ({
  formRef,
  productData,
  setProductData,
  shipmentId,
  withComponent = false,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [suppInfoRequired, setSuppInfoRequired] = useState<boolean>(false);
  const [labelGrades, setLabelGrades] = useState([]);
  const [productCategory, setProductCategory] = useState<any[]>([]);
  const [shipmentOptions, setShipmentOptions] = useState<Option[]>([]);
  const { executeGet } = useApi();
  const { TE_ID, formtype } = useParams();
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const [productMetrics, setProductMetrics] = useState([]);
  const {
    setProductCategoriesStore,
    setProductDetailCodeStore,
    getTCStandardGOTS,
  } = useTransactionCertificateStore();
  const hasSubmitTc: boolean = formtype === "submited-tc";

  const { setDeleteSave } = useFormActions("certified-products");
  // const [productData, setProductData] = useState<ITCProduct>(productData);
  const loadFormData = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_SHIPMENTS_ENDPOINT_V4, {
        tcId: TE_ID,
      });
      const result = response?.data.response?.flatMap((shipment: any) =>
        shipment.tcShipmentInformation?.map((detail: any) => ({
          value: detail.tcShipmentDocNo ?? "",
          label: detail.tcShipmentDocNo ?? "",
        })) || []
      ) || [];
      setShipmentOptions(result);
      if (result?.length === 1) {
        if (getTCStandardGOTS) {
          productData.tcProductShipmentDocNo = result?.map((r: any) => r.value);
        }
      }
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
    }
  };

  useEffect(() => {
    const getLabelGrades = async () => {
      try {
        const result: any = await dtrackItService.getStandradLabelGrade();
        // Utills.cl(`result ${JSON.stringify(result)}`)
        const lblGrades = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
          description: t.description_1,
        }));
        Utills.cl(`lblGrades ${JSON.stringify(lblGrades)}`);
        setLabelGrades(lblGrades);
        Utills.cl(`lblGrades ${lblGrades}`);
      } catch (err) {
        Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
      }
    };

    const getProductCategory = async () => {
      try {
        // DAT-501 "tc Product Category Code": dropdown values shall be derived
        // from the Seller SC, so only the seller-SC-scoped list is offered (no
        // full-reference-list fallback). An empty list means the seller SC
        // carries no product-category scope and a category cannot be picked
        // until the SC data is corrected.
        let result: any = null;
        if (TE_ID) {
          try {
            result = await dtrackItService.getProductCategoryByTcId(TE_ID);
          } catch (scopedErr) {
            Utills.cl(`Scoped product category lookup failed: ${scopedErr}`);
            result = null;
          }
        }

        const prdtCategory = dedupeOptions(
          (result?.data || []).map((t: any) => ({
            value: t.code_1,
            label: `${t.description_1} (${t.code_1})`,
            description: t.description_1,
          }))
        );
        Utills.cl(`prdtCategory ${JSON.stringify(prdtCategory)}`);
        setProductCategory(prdtCategory);
        setProductCategoriesStore(prdtCategory);
      } catch (err) {
        Utills.cl(`Error in Fetching Product Category Code ${err}`);
      }
    };

    const getProductDetails = async () => {
      try {
        // DAT-501 "tc Product Detail Code": same Seller-SC-scoped rule as the
        // category dropdown — no full-reference-list fallback.
        let result: any = null;
        if (TE_ID) {
          try {
            result = await dtrackItService.getProductDetailsCodeByTcId(TE_ID);
          } catch (scopedErr) {
            Utills.cl(`Scoped product detail lookup failed: ${scopedErr}`);
            result = null;
          }
        }

        Utills.cl(`result getProductDetails ${JSON.stringify(result)}`);

        const prdtDetails = dedupeOptions(
          (result?.data || []).map((t: any) => ({
            value: t.code_1,
            label: `${t.description_1} (${t.code_1})`,
            description: t.description_1,
          }))
        );
        Utills.cl(`prdtDetails ${JSON.stringify(prdtDetails)}`);
        setProductDetails(prdtDetails);
        setProductDetailCodeStore(prdtDetails);
      } catch (err) {
        Utills.cl(`Error in Fetching Product Detail Code ${err}`);
      }
    };

    const getMetricsValues = async () => {
      try {
        const result: any = await dtrackItService.getMetricsValues();

        Utills.cl(`result getMetrics ${JSON.stringify(result)}`);

        const prdtDetails = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
          description: t.description_1,
        }));
        setProductMetrics(prdtDetails);
      } catch (err) {
        Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
      }
    };

    TE_ID && loadFormData();

    getLabelGrades();
    getProductCategory();
    getProductDetails();
    getMetricsValues();
  }, []);

  // Second useEffect to detect added/removed values

  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setProductData(productData);
  };
  const onLabelGradeChange = (e: any) => {
    Utills.cl(`onLabelGradeChange ${e}`);
  };
  const productValidationSchema = Yup.object().shape({
    // Shipment and Product Information
    // associatedShipmentNo: Yup.string().nullable(),
    tcProductDetailCode: Yup.string()
      .required("Product Details are required")
      .test(
        "in-seller-sc-scope",
        "Product Detail Code is not in the Seller SC scope — select one of the listed codes",
        (value: string | undefined) => isInScope(productDetails, value)
      ),
    tcProductCategoryCode: Yup.string()
      .required("Product Category Code is required")
      .test(
        "in-seller-sc-scope",
        "Product Category Code is not in the Seller SC scope — select one of the listed codes",
        (value: string | undefined) => isInScope(productCategory, value)
      ),

    // In WITH-component mode the Net Shipping Weight is derived from the components
    // (Σ component net + supplementary), so it is hidden and not required here.
    tcProductNetShippingWeight: withComponent
      ? Yup.string().nullable()
      : Yup.string()
          .test(
            "is-max-five-decimal-places",
            "Number must be a valid decimal number with up to 5 decimal places.",
            (value: string | undefined) => {
              if (!value) return true; // Allow empty values
              return /^(0|[0-9]\d*|0\.\d{1,5}|[0-9]\d*\.\d{1,5})$/.test(value);
            }
          )
          .required("Product Net Shipping Weight is required")
          .typeError("Product Net Shipping Weight must be a valid number"),

    tcProductSupplementaryWeight: Yup.string()
      .test(
        "is-max-five-decimal-places",
        "Number must be a valid decimal number with up to 5 decimal places.",
        (value: string | undefined) => {
          if (!value) return true;
          return /^(0|[0-9]\d*|0\.\d{1,5}|[0-9]\d*\.\d{1,5})$/.test(value);
        }
      )
      .test(
        "supplementary-less-than-net",
        "Supplementary Weight must be less than Net Shipping Weight",
        function (value: any) {
          // WITH-component mode hides the Net Shipping Weight field at this step — the net
          // weight is derived later as components-sum + supplementary, so supplementary <
          // net holds by construction and there is nothing to compare against yet.
          if (withComponent) return true;
          const netWeight = parseFloat(this.parent.tcProductNetShippingWeight);
          const suppWeight = parseFloat(value);
          if (isNaN(netWeight) || isNaN(suppWeight)) return true;
          return (netWeight === 0 && suppWeight === 0) || suppWeight < netWeight;
        }
      )
      .nullable(),
    tcProductInfoSupplementaryWeight: Yup.string().when("tcProductSupplementaryWeight", {
      is: (val: any) => {
        if (val === undefined || val === null) return false;
        const str = String(val).trim();
        if (!str) return false;
        // treat '0' or 0 as empty (only require info when meaningful value provided)
        const num = Number(str);
        if (!isNaN(num) && num === 0) return false;
        return true;
      },
      then: Yup.string().required(
        "Supplementary Weight Info is required when Supplementary Weight is provided"
      ),
      otherwise: Yup.string().nullable(),
    }),
    // In WITH-component mode Additives Weight is hidden and defaults to 0 (the product's
    // material weight is the sum of its components), so it is not required here.
    tcProductAdditivesWeight: withComponent
      ? Yup.string().nullable()
      : Yup.string()
          .required("Product Additives Weight is required; enter 0 when not applicable")
          .test(
            "is-max-five-decimal-places",
            "Product Additives Weight must be a valid decimal number with up to 5 decimal places.",
            (value: string | undefined) => {
              if (value === undefined || value === null || value === "") return false;
              return /^(0|[0-9]\d*|0\.\d{1,5}|[0-9]\d*\.\d{1,5})$/.test(value);
            }
          )
          .test(
            "additives-less-than-net",
            "Product Additives Weight must be less than Product Net Shipping Weight",
            function (value: any) {
              const netWeight = parseFloat(this.parent.tcProductNetShippingWeight);
              const additivesWeight = parseFloat(value);
              if (isNaN(netWeight) || isNaN(additivesWeight)) return true;
              return (netWeight === 0 && additivesWeight === 0) || additivesWeight < netWeight;
            }
          ),
    tcProductAdditives: Yup.string()
      .nullable()
      .max(100, "Maximum 100 characters are allowed")
      .when("tcProductAdditivesWeight", {
        is: (val: any) => {
          if (val === undefined || val === null) return false;
          const str = String(val).trim();
          if (!str) return false;
          const num = Number(str);
          return !isNaN(num) && num !== 0;
        },
        then: Yup.string()
          .required("Product Additives is required when Product Additives Weight is not 0")
          .test(
            "not-blank-or-placeholder",
            "Product Additives cannot be blank, na, n/a, not available, or null",
            (value: string | undefined) => !isBlankOrPlaceholder(value)
          )
          .max(100, "Maximum 100 characters are allowed"),
        otherwise: Yup.string().nullable().max(100, "Maximum 100 characters are allowed"),
      }),
    tcProductOrderNo: Yup.array()
      .of(
        Yup.string()
          .required("Order Number is required")
          .test(
            "not-blank-or-placeholder",
            "Order Number cannot be blank, na, n/a, not available, or null",
            (value: string | undefined) => !isBlankOrPlaceholder(value)
          )
          .max(100, "Each Order Number must be at most 100 characters")
      )
      .min(1, "At least one Order Number is required")
      .required("Order Number is required"),

    tcProductArticleNo: Yup.string()
      .required("Article Number is required")
      .test(
        "not-blank-or-placeholder",
        "Article Number cannot be blank, na, n/a, not available, or null",
        (value: string | undefined) => !isBlankOrPlaceholder(value)
      )
      .max(50, "Maximum 50 Characters are allowed"),


    tcProductNumberOfUnits: Yup.string()
      .test(
        "is-valid-integer",
        "Number of Units must be a whole number.",
        (value: string | undefined) => {
          if (!value) return true;
          return /^(0|[1-9]\d*)$/.test(value);
        }
      )
      .required("TC Product Number of Units is required")
      .typeError("TC Product Number of Units must be a valid number"),

    tcProductUnitsMetric: Yup.string()
      .required("Units Metric is required")
      .max(50, "Maximum 50 Characters are allowed"),

    tcProductUnitsMetricOther: Yup.string().when("tcProductUnitsMetric", {
      is: (value: any) => isOtherUnitsMetric(value),
      then: Yup.string()
        .required("Units Metric Other is required when Units Metric is Other")
        .test(
          "not-blank-or-placeholder",
          "Units Metric Other cannot be blank, na, n/a, not available, or null",
          (value: string | undefined) => !isBlankOrPlaceholder(value)
        )
        .max(255, "Maximum 255 Characters are allowed"),
      otherwise: Yup.string()
        .nullable()
        .max(255, "Maximum 255 Characters are allowed"),
    }),

    tcProductInfoAssociatedTc: Yup.string().nullable(),
    tcProductCategoryUserSpecificTerm: Yup.string()
      .nullable()
      .max(255, "Maximum 255 Characters is allowed")
      .when("tcProductCategoryCode", {
        is: (value: string) => value === "PC0038",
        then: Yup.string()
          .required("Category User Specific Term is required when Product Category Code is PC0038")
          .max(255, "Maximum 255 Characters is allowed"),
        otherwise: Yup.string()
          .nullable()
          .test(
            "must-be-blank-unless-category-other",
            "Category User Specific Term must be blank unless Product Category Code is PC0038",
            (value: string | null | undefined) => !value || !String(value).trim()
          )
          .max(255, "Maximum 255 Characters is allowed"),
      }),
    tcProductDetailUserSpecificTerm: Yup.string()
      .nullable()
      .max(255, "Maximum 255 Characters is allowed")
      .when("tcProductDetailCode", {
        is: (value: string) => value === "PD0010",
        then: Yup.string()
          .required("Detail User Specific Term is required when Product Detail Code is PD0010")
          .max(255, "Maximum 255 Characters is allowed"),
        otherwise: Yup.string()
          .nullable()
          .test(
            "must-be-blank-unless-detail-other",
            "Detail User Specific Term must be blank unless Product Detail Code is PD0010",
            (value: string | null | undefined) => !value || !String(value).trim()
          )
          .max(255, "Maximum 255 Characters is allowed"),
      }),
    tcProductProductionDate: Yup.string().nullable(),
    tcProductCategoryDescription: Yup.string().nullable(),
    tcProductDetailDescription: Yup.string().nullable(),

    tcProductShipmentDocNo: Yup.array()
      .of(Yup.string())
      .min(1, "Shipment Doc No is required")
      .required("Shipment Doc No is required"),
  });

  const formConfig = {
    pageName: "certified-products",
    subPageName: "tcProductInformation",
    initialValues: productData,
    enableReinitialize: true,
    validationSchema: productValidationSchema,
    sections: [
      {
        title: "Add Product",
        fields: [
          // Row 1: Article No | Shipment Doc No | Production Date
          {
            label: "Product Article Number",
            name: "tcProductArticleNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            value: productData.tcProductArticleNo,
            props: {
              placeholder: "Enter product article no.",
            },
          },
          {
            label: "Product Shipment Doc No",
            name: "tcProductShipmentDocNo",
            readOnly: hasSubmitTc,
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            props: {
              options: shipmentOptions,
              placeholder: "Select product shipment doc no.",
              multiple: true,
            },
          },
          {
            label: "Product Production Date",
            name: "tcProductProductionDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: productData.tcProductProductionDate,
            props: {
              placeholder: "YYYY-MM-DD",
            },
          },
          // Row 2: Net Shipping Weight | Supplementary Weight | Supplementary Weight Info
          {
            // Hidden in WITH-component mode — derived from the components.
            label: "Product Net Shipping Weight",
            name: "tcProductNetShippingWeight",
            type: "number",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: productData.tcProductNetShippingWeight,
            visible: () => !withComponent,
            props: {
              type: "number",
              placeholder: "Enter product net shipping weight",
            },
          },
          {
            label: "Product Supplementary Weight",
            name: "tcProductSupplementaryWeight",
            type: "number",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: productData.tcProductSupplementaryWeight,
            props: {
              type: "number",
              placeholder: "Enter product supplementary weight",
            },
            events: {
              onChange: (newValue: any, updatedValues: any, setFieldValue: (field: string, value: any) => void) => {
                try {
                  const val = updatedValues?.tcProductSupplementaryWeight;
                  const hasVal = val !== undefined && val !== null && String(val).trim() !== "" && !(Number(String(val).trim()) === 0);
                  setSuppInfoRequired(Boolean(hasVal));
                } catch (e) {
                  setSuppInfoRequired(false);
                }
              }
            },
          },
          {
            label: "Product Supplementary Weight Info",
            name: "tcProductInfoSupplementaryWeight",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: suppInfoRequired,
            value: productData.tcProductInfoSupplementaryWeight,
            props: {
              placeholder: "Enter product supplementary weight info",
            },
          },
          // Row 3: Additives Weight | Additives
          {
            // Hidden in WITH-component mode — defaults to 0 (components have no additives).
            label: "Product Additives Weight",
            name: "tcProductAdditivesWeight",
            type: "number",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: productData.tcProductAdditivesWeight,
            visible: () => !withComponent,
            props: {
              type: "number",
              placeholder: "Enter product additives weight",
            },
          },
          {
            label: "Product Additives",
            name: "tcProductAdditives",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: productData.tcProductAdditives,
            visible: () => !withComponent,
            props: {
              placeholder: "Enter product additives",
            },
          },
          // Row 4: Order Number (chips) | Number of Units | Units Metric | Units Metric Other
          {
            label: "Product Order Number",
            name: "tcProductOrderNo",
            type: "chips",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: productData.tcProductOrderNo,
            props: {
              placeholder: "Enter product order no. (Press Enter on keyboard after each value)",
            },
          },
          {
            label: "Product Number of Units",
            name: "tcProductNumberOfUnits",
            type: "text",
            gridSize: { xs: 12, md: 2, lg: 2 },
            required: true,
            value: productData.tcProductNumberOfUnits,
            props: {
              placeholder: "Enter product number of units",
              type: "number",
            },
          },
          {
            label: "Product Units Metric",
            name: "tcProductUnitsMetric",
            type: "select",
            gridSize: { xs: 12, md: 2, lg: 2 },
            required: true,
            value: productData.tcProductUnitsMetric,
            props: {
              options: productMetrics,
              placeholder: "Select product unit metric",
            },
          },
          {
            label: "Product Units Metric Other",
            name: "tcProductUnitsMetricOther",
            type: "text",
            gridSize: { xs: 12, md: 2, lg: 2 },
            required: true,
            value: productData.tcProductUnitsMetricOther,
            visible: (values: any) => isOtherUnitsMetric(values?.tcProductUnitsMetric),
            props: {
              placeholder: "Enter product units metric other",
            },
          },
          // Row 5: Category Code | Category User Specific Term
          {
            label: "Product Category Code",
            name: "tcProductCategoryCode",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: productData.tcProductCategoryCode,
            events: {
              onChange: (newValue: string, updatedValues: any, setFieldValue: (field: string, value: any) => void) => {
                const selectedOption: any = productDetails.find((opt: any) => opt.value === newValue);
                if (selectedOption) {
                  setFieldValue("tcProductDetailDescription", selectedOption?.description);
                } else {
                  setFieldValue("tcProductDetailDescription", "");
                }
              },
            },
            props: {
              enableSearch: true,
              options: productCategory,
              placeholder: "Select product Category code",
            },
          },
          {
            label: "Product Category Code User Specific Term",
            name: "tcProductCategoryUserSpecificTerm",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: productData.tcProductCategoryUserSpecificTerm,
            props: {
              placeholder: "Enter product category code user specific term",
            },
          },
          // Row 6: Detail Code | Detail User Specific Term
          {
            label: "Product Detail Code",
            name: "tcProductDetailCode",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: productData.tcProductDetailCode,
            events: {
              onChange: (newValue: string, updatedValues: any, setFieldValue: (field: string, value: any) => void) => {
                const selectedOption: any = productDetails.find((opt: any) => opt.value === newValue);
                if (selectedOption) {
                  setFieldValue("tcProductDetailDescription", selectedOption.description);
                } else {
                  setFieldValue("tcProductDetailDescription", "");
                }
              },
            },
            props: {
              enableSearch: true,
              options: productDetails,
              placeholder: "Select product Detail code",
            },
          },
          {
            label: "Product Detail Code User Specific Term",
            name: "tcProductDetailUserSpecificTerm",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: productData.tcProductDetailUserSpecificTerm,
            props: {
              placeholder: "Enter product detail code user specific term",
            },
          },
          // {
          //   label: "Product Info Associated Tc", //label: 'Associated Product Info', renamed to match table header
          //   name: "tcProductInfoAssociatedTc",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: false,
          //   value: productData.tcProductInfoAssociatedTc,
          //   props: {
          //     placeholder: "Enter Associated Product Info",
          //   },
          // },
          // {
          //   label: 'Product Certified Weight',
          //   name: 'tcProductCertifiedWeight',
          //   type: 'text',
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: !getTCStandardGOTS ? true : false,
          //   props: {
          //     type: 'number',
          //     placeholder: 'Enter TC Product Certified Weight',
          //   },
          // },
          // {
          //   label: 'Product Standard (Label Grade)',
          //   name: 'tcProductStandardLabelGrade',
          //   type: 'select',
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: !getTCStandardGOTS ? true : false,
          //   events: { onChange: onLabelGradeChange },
          //   // value: productData?.tcProductStandardInformation?.tcProductStandardLabelGrade,
          //   props: {
          //     options: labelGrades,
          //     placeholder: 'Select TC Product Standard (Label Grade)',
          //   },
          // },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      Utills.cl("Form Submitted: Product Info", values);
      setProductData(values);
      toggleDrawer();
      // resetForm();
    },
  };

  return (
    <>
      {showWarning && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "#FFF3E0",
            borderRadius: 1,
            border: "1px solid #FFB74D",
          }}
        >
          <Typography color="warning.dark">
            Note: Any changes to the Certified Organization (CO) details should
            be made in the "Certified Organization Details" tab.
          </Typography>
        </Box>
      )}
      {/* <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
        <StyledButton variant="contained" onClick={toggleDrawer}>Add Product</StyledButton>
      </Box>
      <Box display="grid">
      <CustomTable
        columns={columns}
        data={data}
        enableTopToolbar={false}
        enableBottomToolbar={true}
        enableRowActions={true}
        renderRowActions={({ row }) => (
          <ActionsMenu
            // onEdit={() => handleEdit(row)}
            // onDelete={() => handleDelete(row)}
            row={row}
          />
        )}
      />
      </Box> */}
      {/* <ModalPopup open={drawerOpen} onClose={toggleDrawer} onSave={handleSubmit}> */}
      <FormBuilder
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
      {/* </ModalPopup> */}
    </>
  );
};

export default ProductInfo;
